import React, { useEffect, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Menu,
  MenuItem,
  Radio,
  RadioGroup,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import config from '../config';
import { useAuth } from '../contexts/AuthContext';
import { getCategoryColor } from '../theme';

interface AIVoice { greeting: string; persona: 'user' | 'assistant' }
const DEFAULT_VOICE: AIVoice = { greeting: 'Hi {name},', persona: 'assistant' };
const GREETING_PRESETS = ['Hi {name},', 'Hello {name},', 'Dear {name},'];

interface VoicePreview { key: string; label: string; audience: string; subject: string; html: string }
interface Impact {
  applicable: number;
  not_applicable: number;
}

interface AssistantSettingsDialogProps {
  open: boolean;
  onClose: () => void;
  currentDays: number;
  currentRecipients: Recipients;
  currentFunctions: AssistantFunctions;
  token: string;
  onSaved: (days: number, flowsUpdated: number | null, recipients: Recipients, functions: AssistantFunctions) => void;
}

export type AssistantFunctions = Record<string, boolean>;

export const ASSISTANT_FUNCTIONS: { key: string; label: string; description: string }[] = [
  { key: 'calendar', label: 'Adds reminders to calendar', description: 'Puts each reminder in your Google Calendar.' },
  { key: 'contacts', label: 'Contacts providers & tenants', description: 'Drafts and sends the emails in each flow.' },
  { key: 'booking', label: 'Books the appointment', description: 'Coordinates a visit time between provider and tenant.' },
  { key: 'renewals', label: 'Renews your record', description: 'Updates the certificate once the new one arrives.' },
];

export const functionOn = (f: AssistantFunctions, key: string) => f[key] !== false;

export interface FunctionPrompt { key: 'calendar' | 'contacts'; enable: boolean; count: number }

export const promptCopy = (p: FunctionPrompt) => {
  const n = p.count;
  const plural = (one: string, many: string) => (n === 1 ? one : many);
  if (p.key === 'calendar') {
    return p.enable
      ? { title: 'Add your existing reminders to your calendar?', body: `${n} upcoming ${plural('reminder has', 'reminders have')} no calendar event. I can add ${plural('it', 'them')} to your Google Calendar.`, action: 'Add to calendar' }
      : { title: 'Remove your existing reminders from your calendar?', body: `${n} ${plural('reminder has a calendar event', 'reminders have calendar events')}. I can delete ${plural('it', 'them')} from your Google Calendar; the reminders and their flows stay in the AI assistant.`, action: 'Remove from calendar' };
  }
  return p.enable
    ? { title: 'Create emails for your existing flows?', body: `${n} upcoming ${plural('flow has', 'flows have')} no emails yet. I can draft and schedule emails to your providers and tenants for ${plural('it', 'them')}.`, action: 'Create emails' }
    : { title: 'Remove the emails from your existing flows?', body: `${n} ${plural('flow has', 'flows have')} unsent emails to providers or tenants. I can delete these drafts and cancel their scheduled sends; emails already sent stay, and the reminders stay.`, action: 'Remove emails' };
};

const TABS = [
  { key: 'voice', label: 'AI Voice' },
  { key: 'timers', label: 'Timers' },
  { key: 'emails', label: 'Emails' },
] as const;

type RecipientMode = 'both' | 'provider' | 'tenant' | 'none';
type Recipients = Record<string, RecipientMode>;

const CATEGORIES = [
  'Gas Certificate',
  'Electrical Certificate',
  'Energy Certificate',
  'Fire Certificate',
  'Smoke Certificate',
  'Emergency Lighting Certificate',
  'Insurance Certificate',
  'Licence Certificate',
  'Deposit Protection Certificate',
  'Other',
];

const BOTH_BY_DEFAULT = [
  'Gas Certificate',
  'Electrical Certificate',
  'Energy Certificate',
  'Fire Certificate',
  'Smoke Certificate',
  'Emergency Lighting Certificate',
];

const defaultMode = (cat: string): RecipientMode => (BOTH_BY_DEFAULT.includes(cat) ? 'both' : 'provider');

const MODE_LABELS: Record<RecipientMode, string> = {
  both: 'Provider and tenant',
  provider: 'Provider only',
  tenant: 'Tenant only',
  none: 'Not assigned (no flow)',
};

const normalise = (r: Recipients): Recipients =>
  Object.fromEntries(Object.entries(r).filter(([cat, mode]) => mode !== defaultMode(cat)));

// null = not assigned to any group, so no flow is started for that type
type Assignments = Record<string, RecipientMode | null>;

const toAssignments = (r: Recipients): Assignments =>
  Object.fromEntries(CATEGORIES.map(c => [c, r[c] === 'none' ? null : (r[c] ?? defaultMode(c))]));

const GROUP_ORDER: RecipientMode[] = ['provider', 'both', 'tenant'];

const plural = (n: number, unit: string) => `${n} ${unit}${n === 1 ? '' : 's'}`;

const describeDays = (total: number) => {
  const weeks = Math.floor(total / 7);
  const days = total % 7;
  if (weeks === 0) return plural(days, 'day');
  return days === 0 ? plural(weeks, 'week') : `${plural(weeks, 'week')} and ${plural(days, 'day')}`;
};

const AssistantSettingsDialog: React.FC<AssistantSettingsDialogProps> = ({ open, onClose, currentDays, currentRecipients, currentFunctions, token, onSaved }) => {
  const [days, setDays] = useState(String(currentDays));
  const [chase, setChase] = useState('3');
  const [tab, setTab] = useState<'timers' | 'emails' | 'voice'>('voice');
  const [customGreeting, setCustomGreeting] = useState(false);
  const [voice, setVoice] = useState<AIVoice>(DEFAULT_VOICE);
  const [previews, setPreviews] = useState<VoicePreview[]>([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const { palette } = useTheme();
  const { user } = useAuth();
  const [functions, setFunctions] = useState<AssistantFunctions>(currentFunctions);
  const [assignments, setAssignments] = useState<Assignments>(toAssignments(currentRecipients));
  const [addMenu, setAddMenu] = useState<{ anchor: HTMLElement; mode: RecipientMode } | null>(null);
  const [dragCat, setDragCat] = useState<string | null>(null);
  const [dragOverMode, setDragOverMode] = useState<RecipientMode | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [impact, setImpact] = useState<Impact | null>(null);
  const [prompts, setPrompts] = useState<FunctionPrompt[]>([]);
  const [promptUpdated, setPromptUpdated] = useState<number | null>(null);
  const calendarPrompt = prompts[0] ?? null;

  useEffect(() => {
    if (open) {
      setDays(String(currentDays));
      setTab('voice');
      setFunctions(currentFunctions);
      setAssignments(toAssignments(currentRecipients));
      setAddMenu(null);
      setError(null);
      setImpact(null);
      setPrompts([]);
      setBusy(false);
      setSettingsLoaded(false);
      fetch(`${config.apiUrl}/api/assistant/settings`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => {
          if (!r.ok) throw new Error('Could not load your settings. Close Personalize and try again.');
          return r.json();
        })
        .then(data => {
          if (data?.chase_after_days) setChase(String(data.chase_after_days));
          if (data?.ai_voice) {
            setVoice(data.ai_voice);
            setCustomGreeting(!GREETING_PRESETS.includes(data.ai_voice.greeting));
          }
          setSettingsLoaded(true);
        })
        .catch(() => setError('Could not load your settings. Close Personalize and try again.'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const voiceValid = voice.greeting.trim().length > 0 && voice.greeting.length <= 60;

  // Re-render the sample emails shortly after the voice settings stop changing
  useEffect(() => {
    if (!open || tab !== 'voice' || !voiceValid) return;
    const timer = setTimeout(async () => {
      setPreviewLoading(true);
      setPreviewError(null);
      try {
        const res = await fetch(`${config.apiUrl}/api/assistant/voice-preview`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ ai_voice: { ...voice, greeting: voice.greeting.trim() } }),
        });
        if (!res.ok) throw new Error((await res.json()).error || 'Could not load the previews');
        setPreviews((await res.json()).previews);
      } catch (e: any) {
        setPreviewError(e.message || 'Could not load the previews');
      } finally {
        setPreviewLoading(false);
      }
    }, 500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, tab, voice, voiceValid]);

  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
  const parsed = Number(days);
  const valid = Number.isInteger(parsed) && parsed >= 1 && parsed <= 365;
  const daysChanged = valid && parsed !== currentDays;
  const chaseParsed = Number(chase);
  const chaseValid = Number.isInteger(chaseParsed) && chaseParsed >= 1 && chaseParsed <= 60;
  const unassigned = CATEGORIES.filter(c => assignments[c] === null);
  const recipients: Recipients = normalise(
    Object.fromEntries(CATEGORIES.map(c => [c, assignments[c] ?? 'none']))
  );

  const assign = (cat: string, mode: RecipientMode | null) =>
    setAssignments(prev => ({ ...prev, [cat]: mode }));

  const save = async (applyToExisting: boolean) => {
    setBusy(true);
    setError(null);
    try {
      const saveRes = await fetch(`${config.apiUrl}/api/assistant/settings`, {
        method: 'PUT', headers,
        body: JSON.stringify({
          reminder_lead_days: parsed,
          chase_after_days: chaseParsed,
          flow_recipients: recipients,
          functions: Object.fromEntries(ASSISTANT_FUNCTIONS.map(f => [f.key, functionOn(functions, f.key)])),
          ...(voiceValid ? { ai_voice: { ...voice, greeting: voice.greeting.trim() } } : {}),
        }),
      });
      if (!saveRes.ok) throw new Error((await saveRes.json()).error || 'Could not save the settings');

      let updated: number | null = null;
      if (applyToExisting) {
        const applyRes = await fetch(`${config.apiUrl}/api/assistant/apply-lead-days`, {
          method: 'POST', headers, body: JSON.stringify({ days: parsed }),
        });
        if (!applyRes.ok) throw new Error((await applyRes.json()).error || 'Could not update the existing flows');
        updated = (await applyRes.json()).updated;
      }

      const queued: FunctionPrompt[] = [];
      for (const key of ['calendar', 'contacts'] as const) {
        const enable = functionOn(functions, key);
        if (enable === functionOn(currentFunctions, key)) continue;
        const impactRes = await fetch(`${config.apiUrl}/api/assistant/${key}-impact?enabled=${enable}`, { headers });
        const count = impactRes.ok ? (await impactRes.json()).count : 0;
        if (count > 0) queued.push({ key, enable, count });
      }
      if (queued.length > 0) {
        setPrompts(queued);
        setPromptUpdated(updated);
        setBusy(false);
        return;
      }
      onSaved(parsed, updated, recipients, functions);
      onClose();
    } catch (e: any) {
      setError(e.message || 'Something went wrong');
      setBusy(false);
    }
  };

  const finishPrompt = async (apply: boolean) => {
    if (!calendarPrompt) return;
    setBusy(true);
    setError(null);
    try {
      if (apply) {
        const res = await fetch(`${config.apiUrl}/api/assistant/apply-${calendarPrompt.key}`, {
          method: 'POST', headers, body: JSON.stringify({ enabled: calendarPrompt.enable }),
        });
        if (!res.ok) throw new Error((await res.json()).error || 'Could not update the existing flows');
      }
      if (prompts.length > 1) {
        setPrompts(prompts.slice(1));
        setBusy(false);
        return;
      }
      onSaved(parsed, promptUpdated, recipients, functions);
      onClose();
    } catch (e: any) {
      setError(e.message || 'Something went wrong');
      setBusy(false);
    }
  };

  const handleSave = async () => {
    if (!settingsLoaded || !valid || !chaseValid) return;
    if (!daysChanged) {
      await save(false);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const res = await fetch(`${config.apiUrl}/api/assistant/lead-days-impact?days=${parsed}`, { headers });
      if (!res.ok) throw new Error('Could not check the existing flows');
      const data: Impact = await res.json();
      if (data.applicable === 0) {
        await save(false);
        return;
      }
      setImpact(data);
      setBusy(false);
    } catch (e: any) {
      setError(e.message || 'Something went wrong');
      setBusy(false);
    }
  };

  const shortName = (cat: string) => cat.replace(' Certificate', '');
  const chipSx = (cat: string) => ({
    backgroundColor: getCategoryColor(cat), color: 'white', fontWeight: 600,
    '& .MuiChip-deleteIcon': { color: 'rgba(255,255,255,0.8)', '&:hover': { color: 'white' } },
  });

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>Personalize Mill, your AI agent</DialogTitle>
      <DialogContent>
        {calendarPrompt ? (
          <Box>
            <Typography variant="body1" sx={{ mb: 1 }}>{promptCopy(calendarPrompt).title}</Typography>
            <Typography variant="body2" color="text.secondary">{promptCopy(calendarPrompt).body}</Typography>
          </Box>
        ) : impact ? (
          <Box>
            <Typography variant="body1" sx={{ mb: 1 }}>
              Update your existing flows to the new setting?
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {impact.applicable} flow{impact.applicable === 1 ? '' : 's'} can be moved to start {parsed} days before expiry.
              {impact.not_applicable > 0 && ` ${impact.not_applicable} can't be changed (already started, or the new start date would be in the past) and will stay as they are.`}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ pt: 1 }}>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              {TABS.map(t => {
                const isSelected = tab === t.key;
                return (
                  <Box
                    key={t.key}
                    onClick={() => setTab(t.key)}
                    sx={{
                      flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', px: 1.5, py: 0.75,
                      borderRadius: '10px 10px 0 0', cursor: 'pointer', position: 'relative', zIndex: isSelected ? 1 : 0,
                      bgcolor: isSelected ? palette.tokens.white : 'transparent',
                      border: isSelected ? `1px solid ${palette.tokens.hairline}` : '1px solid transparent',
                      borderBottom: isSelected ? `1px solid ${palette.tokens.white}` : 'none',
                      mb: isSelected ? '-1px' : 0,
                      '&:hover': !isSelected ? { bgcolor: alpha(palette.tokens.white, 0.5) } : {},
                    }}
                  >
                    <Typography sx={{ fontSize: '0.85rem', fontWeight: isSelected ? 700 : 500, color: isSelected ? palette.tokens.ink : palette.tokens.muted }}>
                      {t.label}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
            <Box sx={{ bgcolor: palette.tokens.white, border: `1px solid ${palette.tokens.hairline}`, borderRadius: '0 8px 8px 8px', p: 2, height: 440, overflowY: 'auto' }}>
            {tab === 'timers' && (<>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Choose when the assistant starts a flow for each certificate.
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <TextField
                label="Start a flow this many days before expiry"
                type="number"
                size="small"
                sx={{ width: 'calc(43ch + 28px)', flexShrink: 0 }}
                value={days}
                onChange={(e) => setDays(e.target.value)}
                error={days !== '' && !valid}
                inputProps={{ min: 1, max: 365 }}
                disabled={busy}
              />
              {valid && (
                <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                  {describeDays(parsed)}
                </Typography>
              )}
            </Box>
            {days !== '' && !valid && (
              <Typography variant="caption" color="error">Enter a whole number between 1 and 365</Typography>
            )}
            <Typography variant="body2" color="text.secondary" sx={{ mt: 3, mb: 2 }}>
              If the provider or tenant has not replied after this many days, Mill sends a follow-up email with a more urgent tone.
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <TextField
                label="Chase after this many days without a reply"
                type="number"
                size="small"
                sx={{ width: 'calc(43ch + 28px)', flexShrink: 0 }}
                value={chase}
                onChange={(e) => setChase(e.target.value)}
                error={chase !== '' && !chaseValid}
                inputProps={{ min: 1, max: 60 }}
                disabled={busy}
              />
              {chaseValid && (
                <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                  {describeDays(chaseParsed)}
                </Typography>
              )}
            </Box>
            {chase !== '' && !chaseValid && (
              <Typography variant="caption" color="error">Enter a whole number between 1 and 60</Typography>
            )}
            </>)}

            {tab === 'emails' && (<>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Pick a certificate type and choose who gets emailed.
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Certificate types</TableCell>
                  <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Parties to email</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {GROUP_ORDER.map(mode => (
                  <TableRow
                    key={mode}
                    onDragOver={dragCat ? (e) => { e.preventDefault(); setDragOverMode(mode); } : undefined}
                    onDragLeave={() => setDragOverMode(null)}
                    onDrop={(e) => {
                      e.preventDefault();
                      if (dragCat) assign(dragCat, mode);
                      setDragCat(null);
                      setDragOverMode(null);
                    }}
                    sx={{ bgcolor: dragOverMode === mode ? 'action.hover' : undefined }}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, alignItems: 'center' }}>
                        {CATEGORIES.filter(c => assignments[c] === mode).map(c => (
                          <Chip
                            key={c}
                            size="small"
                            label={shortName(c)}
                            draggable={!busy}
                            onDragStart={() => setDragCat(c)}
                            onDragEnd={() => { setDragCat(null); setDragOverMode(null); }}
                            sx={{ ...chipSx(c), cursor: 'grab' }}
                            onDelete={busy ? undefined : () => assign(c, null)}
                          />
                        ))}
                        <Chip
                          size="small"
                          variant="outlined"
                          label="+ Add"
                          disabled={busy || CATEGORIES.every(c => assignments[c] === mode)}
                          onClick={(e) => setAddMenu({ anchor: e.currentTarget, mode })}
                        />
                      </Box>
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap', verticalAlign: 'top' }}>{MODE_LABELS[mode]}</TableCell>
                  </TableRow>
                ))}
                {(
                  <TableRow
                    onDragOver={dragCat ? (e) => { e.preventDefault(); setDragOverMode('none'); } : undefined}
                    onDragLeave={() => setDragOverMode(null)}
                    onDrop={(e) => {
                      e.preventDefault();
                      if (dragCat) assign(dragCat, null);
                      setDragCat(null);
                      setDragOverMode(null);
                    }}
                    sx={{ bgcolor: dragOverMode === 'none' ? 'action.hover' : undefined }}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                        {unassigned.map(c => (
                          <Chip
                            key={c}
                            size="small"
                            label={shortName(c)}
                            draggable
                            onDragStart={() => setDragCat(c)}
                            onDragEnd={() => { setDragCat(null); setDragOverMode(null); }}
                            sx={{ ...chipSx(c), cursor: 'grab' }}
                          />
                        ))}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ verticalAlign: 'top' }}>{MODE_LABELS.none}</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            <Menu anchorEl={addMenu?.anchor} open={Boolean(addMenu)} onClose={() => setAddMenu(null)}>
              {addMenu && CATEGORIES.filter(c => assignments[c] !== addMenu.mode).map(c => (
                <MenuItem key={c} onClick={() => { assign(c, addMenu.mode); setAddMenu(null); }}>
                  {c}{assignments[c] ? ` (now: ${MODE_LABELS[assignments[c] as RecipientMode]})` : ''}
                </MenuItem>
              ))}
            </Menu>
            </>)}

            {tab === 'voice' && (<>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>How should the assistant greet people?</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, my: 1 }}>
              {GREETING_PRESETS.map(g => (
                <Chip
                  key={g}
                  size="small"
                  label={g.replace('{name}', '[name]')}
                  color={!customGreeting && voice.greeting === g ? 'primary' : 'default'}
                  onClick={() => { setCustomGreeting(false); setVoice(v => ({ ...v, greeting: g })); }}
                />
              ))}
              <Chip
                size="small"
                label="Other"
                color={customGreeting ? 'primary' : 'default'}
                onClick={() => setCustomGreeting(true)}
              />
            </Box>
            {customGreeting && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 1.5 }}>
              <TextField
                size="small"
                sx={{ width: `calc(${Math.max(voice.greeting.length, 14) * 1.3}ch + 28px)`, flexShrink: 0 }}
                placeholder="Good morning {name},"
                value={voice.greeting}
                onChange={(e) => setVoice(v => ({ ...v, greeting: e.target.value }))}
                error={!voiceValid}
                inputProps={{ maxLength: 60 }}
                disabled={busy}
              />
              <Typography variant="body2" color={voiceValid ? 'text.secondary' : 'error'}>
                {voiceValid ? "Use {name} as a placeholder for the recipient's name." : 'Enter a greeting of up to 60 characters'}
              </Typography>
            </Box>
            )}

            <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 3 }}>Who am I speaking as?</Typography>
            <RadioGroup value={voice.persona} onChange={(e) => setVoice(v => ({ ...v, persona: e.target.value as AIVoice['persona'] }))}>
              <FormControlLabel value="assistant" control={<Radio size="small" />} disabled={busy}
                label={<Typography variant="body2"><b>As Mill, an AI assistant.</b> Emails say they come from Mill, your AI assistant, writing on your behalf.</Typography>} />
              <FormControlLabel value="user" control={<Radio size="small" />} disabled={busy}
                label={<Typography variant="body2"><b>As me, {user?.name || 'myself'}.</b> Emails read as if you wrote them, signed with your name.</Typography>} />
            </RadioGroup>
            <Typography variant="caption" color="text.secondary">
              Either way, every email ends with a note that it was AI generated by MillOS.
              {' '}Saving also applies your AI Voice to existing unsent drafts, without changing their send dates or links.
            </Typography>

            <Divider sx={{ my: 2 }} />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Preview of your emails</Typography>
              {previewLoading && <CircularProgress size={14} />}
            </Box>
            {previewError && <Alert severity="error" sx={{ mb: 1 }}>{previewError}</Alert>}
            {previews.map(p => (
              <Accordion key={p.key} disableGutters variant="outlined" sx={{ '&:before': { display: 'none' } }}>
                <AccordionSummary>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{p.label}</Typography>
                    <Typography variant="caption" color="text.secondary">To: {p.audience} · Subject: {p.subject}</Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <iframe title={p.label} sandbox="" srcDoc={`<style>a{pointer-events:none;cursor:default}</style><body style="font-family:Arial,sans-serif;font-size:14px;color:#222;margin:12px">${p.html}</body>`}
                    style={{ width: '100%', height: 360, border: `1px solid ${palette.tokens.hairline}`, borderRadius: 6 }} />
                </AccordionDetails>
              </Accordion>
            ))}
            </>)}
            </Box>
          </Box>
        )}
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions>
        {calendarPrompt ? (
          <>
            <Button onClick={() => finishPrompt(false)} disabled={busy}>Leave them as they are</Button>
            <Button variant="contained" onClick={() => finishPrompt(true)} disabled={busy}>
              {busy ? <CircularProgress size={20} /> : promptCopy(calendarPrompt).action}
            </Button>
          </>
        ) : impact ? (
          <>
            <Button onClick={() => save(false)} disabled={busy}>Only new flows</Button>
            <Button variant="contained" onClick={() => save(true)} disabled={busy}>
              {busy ? <CircularProgress size={20} /> : `Update ${impact.applicable} flow${impact.applicable === 1 ? '' : 's'}`}
            </Button>
          </>
        ) : (
          <>
            <Button onClick={onClose} disabled={busy}>Cancel</Button>
            <Button variant="contained" onClick={handleSave} disabled={!settingsLoaded || !valid || !chaseValid || busy}>
              {busy ? <CircularProgress size={20} /> : 'Save'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default AssistantSettingsDialog;
